import moment from "moment";
import { useEffect, useState } from "react";
import { FaSpinner } from "react-icons/fa";
import { GoMute } from "react-icons/go";
import { MdSearch } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import Api from "../../service/http";

const PersonalChatList = ({ chats, typingChat = [], onListsRefresh }) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);

  const handleChatClick = async (chatItem) => {
    const isPrUserSearch =
      debouncedSearch.trim() && chatItem?._userSearch;
    if (isPrUserSearch) {
      try {
        const res = await Api.post("/web/personal-chats", {
          users: [chatItem.id],
          chat_type: "PR",
        });
        const chatId = res?.data?.id;
        if (chatId) {
          onListsRefresh?.();
          navigate(`/personal-chat/${chatId}`);
        }
      } catch {
        /* keep user on list */
      }
      return;
    }
    navigate(`/personal-chat/${chatItem?.id}`);
  };

  const [filteredChats, setFilteredChats] = useState([]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => {
    const q = debouncedSearch.trim();
    if (!q) {
      setFilteredChats(chats ?? []);
      setSearchLoading(false);
      return;
    }

    let cancelled = false;
    setSearchLoading(true);
    const path = `/web/search/users?search=${encodeURIComponent(q)}`;

    Api.get(path)
      .then((res) => {
        if (cancelled) return;
        const raw = Array.isArray(res.data) ? res.data : [];
        const list = raw.map((u) => ({ ...u, _userSearch: true }));
        setFilteredChats(list);
      })
      .catch(() => {
        if (!cancelled) setFilteredChats([]);
      })
      .finally(() => {
        if (!cancelled) setSearchLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [debouncedSearch, chats]);

  return (
    <div className="flex flex-col h-full w-full md:w-72 lg:w-80 bg-white border-r shrink-0">
      <div className="p-4 border-b flex items-center gap-3">
        <div className="relative flex-1">
          <MdSearch
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
            size={20}
          />
          <input
            type="text"
            placeholder="Search users or chats..."
            className="w-full pl-10 pr-4 py-2 rounded-full text-sm outline-none bg-gray-100 text-gray-800 border border-transparent focus:bg-white focus:border-indigo-500"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto custom-scrollbar">
        <div className="p-2">
          {searchLoading ? (
            <div className="flex justify-center py-8 text-gray-500">
              <FaSpinner className="animate-spin" size={22} />
            </div>
          ) : filteredChats?.length > 0 ? (
            filteredChats.map((chatItem, i) => {
              const rowChatId = chatItem?._userSearch ? null : chatItem?.id;
              const isTyping =
                !chatItem?._userSearch &&
                typingChat?.map((itm) => itm?.id)?.includes(chatItem?.id);
              const isActive =
                rowChatId &&
                window.location.pathname.includes(String(rowChatId));

              return (
                <div
                  key={chatItem?.id ?? i}
                  className={`flex items-center gap-3 p-3 mb-1 rounded-xl cursor-pointer transition-all duration-200 border ${
                    isActive
                      ? "bg-indigo-600 text-white border-indigo-600 shadow-md"
                      : "border-transparent hover:bg-indigo-50/80 text-gray-700"
                  }`}
                  onClick={() => handleChatClick(chatItem)}
                >
                  <div className="relative shrink-0">
                    <img
                      src={chatItem?.image || "https://placehold.co/400"}
                      className="w-11 h-11 rounded-xl object-cover shadow-sm"
                      alt=""
                    />
                    {/* <div
                      className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 ${
                        isActive ? "border-indigo-600" : "border-white"
                      } bg-green-500`}
                    /> */}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-baseline mb-0.5">
                      <p
                        className={`text-sm font-medium truncate ${
                          isActive ? "text-white" : ""
                        }`}
                      >
                        {chatItem?.name}
                      </p>
                      <span
                        className={`text-[11px] shrink-0 ${
                          isActive ? "text-indigo-100" : "text-gray-400"
                        }`}
                      >
                        {chatItem?.last_msg?.created_at
                          ? moment(chatItem.last_msg.created_at).format(
                              "hh:mm A"
                            )
                          : ""}
                      </span>
                    </div>

                    <div className="flex justify-between items-center">
                      <p
                        className={`text-xs truncate ${
                          isActive
                            ? "text-indigo-100"
                            : isTyping
                              ? "text-indigo-500 font-medium"
                              : "text-gray-500"
                        }`}
                      >
                        {chatItem?._userSearch
                          ? "Tap to open chat"
                          : isTyping
                            ? "Typing..."
                            : chatItem?.last_msg?.message
                              ? chatItem?.last_msg?.message
                              : debouncedSearch
                                ? ""
                                : "No messages yet"}
                      </p>

                      {chatItem?.unread_count > 0 && !isActive && (
                        <span className="ml-2 px-1.5 py-0.5 text-[10px] font-bold bg-red-500 text-white rounded-full min-w-[18px] text-center">
                          {chatItem?.unread_count}
                        </span>
                      )}
                      {chatItem?.mute && !isActive && (
                        <GoMute className="ml-2 text-gray-400" size={14} />
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="p-4 text-center text-gray-500 text-sm">
              No chats found
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PersonalChatList;
